import { fireEvent, render, waitFor, screen } from '@testing-library/preact';

import { ComponentProps } from 'preact';
import { ElementRef } from 'preact/compat';

import { RootEnvironment, RootEnvironmentProvider } from '../../UNSAFE_Environment';
import { expect } from 'chai';
import { spy } from 'sinon';
import { useRef, useCallback, useState } from 'preact/hooks';
import userEvent from '@testing-library/user-event';
import { BaseButton } from '../../UNSAFE_BaseButton';
import { Sheet } from '../Sheet';
import { getClientHints } from '../../utils/PRIVATE_clientHints';
import { Button } from '../../UNSAFE_Button';
import translations from '../../resources/nls/en/bundle';

type ButtonHandle = ElementRef<typeof Button>;

//TODO: JET-63873
function getSheet(elem: Element) {
  return elem.querySelector('#contentId') as HTMLElement;
}
function getSheetContainer(elem: Element) {
  return (elem.querySelector('#contentId') as Element).parentElement as HTMLElement;
}

function getSheetScrim(sheetContainer: Element) {
  return sheetContainer.parentElement?.parentElement?.parentElement?.firstElementChild;
}

const sheetEnv: Partial<RootEnvironment> = {
  translations: { '@oracle/oraclejet-preact': translations },
  mode: 'test'
};

const SheetSimpleComponent = ({
  onClose
}: {
  onClose?: ComponentProps<typeof Sheet>['onClose'];
}) => {
  const ref = useRef<ButtonHandle>(null);

  return (
    <>
      <RootEnvironmentProvider environment={sheetEnv}>
        <BaseButton ref={ref}>Open</BaseButton>
        <Sheet isOpen={true} onClose={onClose}>
          <div
            style={{ display: 'flex', flexDirection: 'column', outlineStyle: 'none' }}
            id="contentId">
            <h1>N1</h1>
            <h1>N2</h1>
            <h1>N3</h1>
            <h1>N4</h1>
          </div>
        </Sheet>
      </RootEnvironmentProvider>
    </>
  );
};

function IntitialFocusTestComponent() {
  const [isOpen, setIsOpen] = useState(false);
  const toggleButtonRef = useRef<ButtonHandle>(null);

  const toggle = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);
  return (
    <RootEnvironmentProvider environment={sheetEnv}>
      <div
        style={{
          paddingTop: '90px',
          paddingBottom: '90px'
        }}>
        <Sheet isOpen={isOpen} initialFocus="firstFocusable">
          <div
            style={{ display: 'flex', flexDirection: 'column', outlineStyle: 'none' }}
            id="contentId">
            <Button aria-label="OK" label="OK"></Button>
          </div>
        </Sheet>
        <Button ref={toggleButtonRef} onAction={toggle} label="Toggle" />
      </div>
    </RootEnvironmentProvider>
  );
}

/**
 * Renders a Sheet with a toggle Button and restores focus to the Button when the Sheet closes.
 */
const ButtonFocusSheetComponent = () => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<ElementRef<typeof Button>>(null);

  const handleSheetClose = () => {
    setOpen(false);
    // Restore focus to the Button component itself
    buttonRef.current?.focus();
  };

  return (
    <RootEnvironmentProvider environment={sheetEnv}>
      <Button ref={buttonRef} onAction={() => setOpen(!open)} />
      <Sheet isOpen={open} onClose={handleSheetClose}>
        <div
          style={{ display: 'flex', flexDirection: 'column', outlineStyle: 'none' }}
          id="contentId">
          Sheet Content For Focus Test
        </div>
      </Sheet>
    </RootEnvironmentProvider>
  );
};

describe('Test Sheet Component', () => {
  it('Sheet content render correctly', async function () {
    const { container } = render(<SheetSimpleComponent />);

    const sheetContent = getSheet(container);

    expect(sheetContent).not.null;
  });

  it('onClose called when Escape or Tab are pressed', async function () {
    const onActionDummyFunction = spy();
    const { container } = render(<SheetSimpleComponent onClose={onActionDummyFunction} />);

    const sheetContent = getSheet(container);
    const sheetContainer = getSheetContainer(container);

    expect(sheetContent).not.null;

    fireEvent.keyDown(sheetContainer, { code: 'Tab' });
    fireEvent.keyDown(sheetContainer, { code: 'Escape' });
    expect(onActionDummyFunction.calledTwice).to.be.true;
  });

  it('onClose called when clicking scrim', async function () {
    const onActionDummyFunction = spy();
    const { container } = render(<SheetSimpleComponent onClose={onActionDummyFunction} />);
    const sheetContent = getSheet(container);
    const sheetContainer = getSheetContainer(container);
    const sheetScrim = getSheetScrim(sheetContainer);

    expect(sheetContent).not.null;

    await userEvent.click(sheetScrim as HTMLElement);

    expect(onActionDummyFunction.called).to.be.true;
  });

  it('onClose called when swipe is done (mobile)', async function () {
    const onActionDummyFunction = spy();
    const { container } = render(<SheetSimpleComponent onClose={onActionDummyFunction} />);
    const sheetContent = getSheet(container);
    const sheetContainer = getSheetContainer(container);
    expect(sheetContent).not.null;

    if (typeof jest === 'undefined' && getClientHints().touchSupport === 'primary') {
      // Touch objects are created so we can replicate this in fake touch events
      // This stuff is based on what useSwipe triggers and could change depending on the final decision
      // on what events to trigger on useSwipe.
      const touch1 = new Touch({
        target: sheetContainer as EventTarget,
        identifier: 0,
        pageX: 386,
        pageY: 224
      });
      const touch2 = new Touch({
        target: sheetContainer as EventTarget,
        identifier: 0,
        pageX: 389,
        pageY: 397
      });
      if (sheetContainer) {
        fireEvent.touchStart(sheetContainer, {
          timeStamp: 10406,
          currentTarget: sheetContainer,
          changedTouches: [touch1]
        });
        fireEvent.touchEnd(sheetContainer, {
          timeStamp: 10472,
          currentTarget: sheetContainer,
          changedTouches: [touch2]
        });
      }
      expect(onActionDummyFunction.called).to.be.true;
    }
  });

  it('Sheet correct focus when initialFocus set as firstFocusable', async function () {
    const { container } = render(<IntitialFocusTestComponent />);
    const anchorButton = screen.getByRole('button');
    expect(anchorButton, 'anchor element found').not.null;
    if (anchorButton) {
      await userEvent.click(anchorButton);
    }
    await waitFor(() => {
      const sheet = getSheet(container);
      expect(sheet).not.to.be.null;
    });
    const okButton = screen.getByLabelText('OK');
    expect(okButton).not.to.be.null;
    if (typeof jest === 'undefined') {
      await waitFor(() => {
        expect(okButton.matches(':focus')).to.be.true;
      });
    }
  });

  it('restores focus to Button after Sheet closes', async function () {
    const { container } = render(<ButtonFocusSheetComponent />);
    const openButton = screen.getByRole('button');
    openButton.focus();
    await userEvent.click(openButton);
    // Need to await for Sheet open animation to complete
    await waitFor(() => {
      const sheetContent = getSheet(container);
      expect(sheetContent).to.not.be.null;
    });
    const sheetContainer = getSheetContainer(container);
    const sheetScrim = getSheetScrim(sheetContainer);
    // Click on scrim to close Sheet
    await userEvent.click(sheetScrim as HTMLElement);

    // Need to await for Sheet close animation to complete and focus restoration
    await waitFor(() => {
      expect(document.activeElement).to.eq(openButton);
    });
  });
});
